import { useEffect, useMemo } from "react";
import { ensureNetworks } from "@/lib/setup/ensureNetworks";
import { inYearRange, mergeNetworks, type PreviewPaper, previewStats } from "@/lib/setup/preview";
import { isSettled, netKey, networksFor, type SeedRef, useSetupStore } from "@/stores/setup-store";
import type { ConceptScope, PaperKeys } from "@/types/concept";

/** Live preview of what trawling `seeds` would bring: the same `ensureNetworks`
 *  path Start uses, nothing posted. Cached per (seed, recency); only new seeds fetch.
 *  `seeds` should be referentially stable (it's an effect dependency). */
export function useSeedPreview(seeds: SeedRef[], scope: ConceptScope | null | undefined) {
  const networks = useSetupStore((s) => s.networks);
  const resolved = useSetupStore((s) => s.resolved);
  const status = useSetupStore((s) => s.status);
  const recency = scope?.recency;

  useEffect(() => {
    void ensureNetworks(seeds, recency);
  }, [seeds, recency]);

  const loaded = useMemo(() => networksFor({ networks, resolved }, seeds, recency), [networks, resolved, seeds, recency]);
  const merged = useMemo(() => mergeNetworks(loaded), [loaded]);
  const stats = useMemo(() => previewStats(merged, scope), [merged, scope]);
  const statusOf = (s: SeedRef) => status[netKey(s.key, recency)];
  const empty = seeds.filter((s) => statusOf(s) === "empty");
  const failed = seeds.filter((s) => statusOf(s) === "failed");
  const retrying = seeds.filter((s) => statusOf(s) === "retrying").length;
  const settled = seeds.filter((s) => isSettled(statusOf(s))).length;
  return { merged, stats, statusOf, settled, empty, failed, retrying };
}

/** In-year-range preview papers not already in the concept: neither the S2 id
 *  nor the (case-insensitive) DOI matches. A paper with neither id counts as new. */
export function newPaperCount(
  merged: Map<string, PreviewPaper>, scope: ConceptScope | null | undefined, keys: PaperKeys,
): number {
  const s2 = new Set(keys.s2_ids);
  const dois = new Set(keys.dois.map((d) => d.toLowerCase()));
  let n = 0;
  for (const { paper } of merged.values()) {
    if (!inYearRange(paper.year, scope)) continue;
    if (paper.paper_id && s2.has(paper.paper_id)) continue;
    if (paper.doi && dois.has(paper.doi.toLowerCase())) continue;
    n++;
  }
  return n;
}
